import { Experience } from '@ninetailed/experience.js-next';
import { IEntry } from 'global';
import { ComponentType, FC, ReactNode, useCallback } from 'react';
import { parseExperiences } from 'utils/ninetailed';

type ResolvedVariant = Record<string, unknown> & {
  id?: string;
  nt_experiences?: unknown[];
};

interface ExperienceWrapperProps<P = any> {
  entry: IEntry;
  component: ComponentType<P>;
  loadingComponent?: ReactNode;
  depth?: number;
}

interface NestedAwareProps {
  entry: IEntry;
  component: ComponentType<ResolvedVariant>;
  depth: number;
  variantProps: ResolvedVariant;
}

const MAX_NESTING_DEPTH = 3;

const NestedAware: FC<NestedAwareProps> = ({ entry, component, depth, variantProps }) => {
  const Component = component;
  const resolvedVariantId = variantProps?.id;
  const isBaselineResolved = !resolvedVariantId || resolvedVariantId === entry.sys.id;
  const nested = variantProps?.nt_experiences;

  if (
    !isBaselineResolved &&
    depth < MAX_NESTING_DEPTH &&
    Array.isArray(nested) &&
    nested.length > 0
  ) {
    const variantAsEntry = {
      sys: {
        id: resolvedVariantId,
        contentType: entry.sys.contentType,
      },
      fields: variantProps,
    } as unknown as IEntry;
    return <ExperienceWrapper entry={variantAsEntry} component={Component} depth={depth + 1} />;
  }
  return <Component {...variantProps} />;
};

/**
 * Wrapper component for Experience. Supports nested experiences: if the resolved
 * variant carries its own nt_experiences, a second resolution pass is run against
 * the variant before rendering the target component.
 */
const ExperienceWrapper: FC<ExperienceWrapperProps> = ({ entry, component, depth = 0 }) => {
  const renderVariant = useCallback(
    (variantProps: ResolvedVariant) => (
      <NestedAware
        entry={entry}
        component={component as unknown as ComponentType<ResolvedVariant>}
        depth={depth}
        variantProps={variantProps}
      />
    ),
    [entry, component, depth]
  );

  return (
    <Experience
      {...entry.fields}
      id={entry.sys.id}
      experiences={parseExperiences({ entry })}
      component={renderVariant}
    />
  );
};

export default ExperienceWrapper;
